// Single axios instance. Request interceptor stamps Bearer; response
// interceptor clears the store on 401 so the UI snaps back to /login.

import axios, { AxiosError, type InternalAxiosRequestConfig } from 'axios';
import { useAuthStore } from '@/stores/auth-store';

const baseURL = import.meta.env.VITE_API_BASE_URL || '';

export const api = axios.create({
  baseURL,
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = useAuthStore.getState().token;
  if (token) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

api.interceptors.response.use(
  (r) => r,
  (err: AxiosError) => {
    if (err.response?.status === 401) {
      useAuthStore.getState().clear();
    }
    return Promise.reject(err);
  },
);

export interface ApiErrorBody {
  error: string;
  message: string;
  details?: unknown;
}

export function extractApiError(err: unknown): string {
  if (err instanceof AxiosError && err.response?.data) {
    const body = err.response.data as Partial<ApiErrorBody>;
    if (body.message) return body.message;
  }
  if (err instanceof Error) return err.message;
  return 'Something went wrong';
}
